import type { AnimationItem } from 'lottie-web'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Check, ChevronDown, Globe } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useDropdown } from '@/hooks/use-dropdown'
import { useTranslation, locales } from '@/i18n'
import { useSharedTranslation } from '@/i18n/shared'
import { localeLabels, localeNames } from '@/i18n/config'

import {
    DROPDOWN_CHECK_CLASS,
    DROPDOWN_MENU_CLASS,
    dropdownChevronClass,
    dropdownRowClass,
    dropdownTriggerClass
} from '@/lib/dropdown-styles'

export function Navbar() {
    const router = useRouter()
    const { t, locale } = useTranslation()
    const s = useSharedTranslation()

    const [menuOpen, setMenuOpen] = useState(false)
    const language = useDropdown()

    const hamburgerRef = useRef<HTMLDivElement>(null)
    const hamburgerAnim = useRef<AnimationItem | null>(null)

    const navLinks = [
        { label: t.nav.templates, href: '/' },
        { label: t.nav.insights, href: '/insights' },
        { label: t.nav.cloneable, href: '/cloneable' },
        { label: t.nav.faq, href: '/faq' },
        { label: t.nav.contact, href: '/contact' }
    ]

    // Hamburger morph (hamburger-to-x) driven by menu state.
    useEffect(() => {
        let anim: AnimationItem | null = null
        let cancelled = false

        import('lottie-web').then(({ default: lottie }) => {
            if (cancelled || !hamburgerRef.current) return

            anim = lottie.loadAnimation({
                container: hamburgerRef.current,
                renderer: 'svg',
                loop: false,
                autoplay: false,
                path: '/lottie/hamburger-to-x.json'
            })

            hamburgerAnim.current = anim
        })

        return () => {
            cancelled = true
            anim?.destroy()
            hamburgerAnim.current = null
        }
    }, [])

    useEffect(() => {
        const anim = hamburgerAnim.current

        if (!anim) return

        anim.setDirection(menuOpen ? 1 : -1)
        anim.play()
    }, [menuOpen])

    // Close the mobile menu on navigation and when resizing up to desktop.
    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        const mq = window.matchMedia('(min-width: 992px)')
        const onChange = () => mq.matches && setMenuOpen(false)

        mq.addEventListener('change', onChange)

        return () => {
            router.events.off('routeChangeStart', close)
            mq.removeEventListener('change', onChange)
        }
    }, [router.events])

    const isCurrent = (href: string) =>
        href === '/' ? router.pathname === '/' : router.pathname.startsWith(href)

    return (
        <div
            className='navbar w-nav'
            data-collapse='medium'
            data-animation='default'
            role='banner'
        >
            <a href='https://app.wedoflow.com' className='hero-banner-link w-inline-block'>
                <div className='hero-banner'>
                    <div className='banner-text'>
                        {t.nav.topBanner}
                        <strong> →</strong>
                    </div>
                </div>
            </a>

            <a href='https://l.azwedo.com/webflow-goat-slider' className='notification-bars w-inline-block'>
                <div>
                    <span className='notification-second-row-mobile'>{t.nav.notification}</span>
                </div>
            </a>

            <div className='container nav-container'>
                <div className='nav-menu-container'>
                    <Link href='/' className='nav-brand-link w-nav-brand'>
                        <img src='https://wedoflow.cdn.modulify.website/images/Nav-Logo-White.svg' loading='lazy' alt='Wedoflow' className='brand-link' />
                    </Link>

                    <nav
                        className='nav-menu w-nav-menu'
                        data-nav-menu-open={menuOpen ? '' : undefined}
                    >
                        <div className='nav-menu-links-holder'>
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    href={link.href}
                                    className={cn('link-block nav-menu-link w-inline-block', isCurrent(link.href) && 'w--current')}
                                >
                                    <div>{link.label}</div>
                                    <div className='link-block-underline' />
                                </Link>
                            ))}
                            <div className='div-block-5'>
                                <div className='nav-menu-new-badge'>{s.ui.navNewBadge}</div>
                                <a href='https://app.wedoflow.com?r=wedoflow.com' className='link-block nav-menu-link w-inline-block'>
                                    <div>{t.nav.allAccess}</div>
                                    <div className='link-block-underline' />
                                </a>
                            </div>
                        </div>
                        <div className='nav-button-holder'>
                            <a href='https://webflow.com/templates/designers/azwedo' target='_blank' rel='noreferrer' className='button nav-button w-inline-block'>
                                <img src='https://wedoflow.cdn.modulify.website/images/Webflow-Badge.svg' loading='lazy' alt='' />
                                <div>{t.nav.store}</div>
                            </a>
                        </div>
                    </nav>

                    <div className='languages'>
                        {/* Inline zIndex: Webflow ships a `.relative { z-index: 1 }` utility that
                            would otherwise cap Tailwind's z-* classes, letting the open menu slip
                            behind the page content below the navbar. */}
                        <div
                            ref={language.ref}
                            className='language-dropdown relative'
                            style={{ zIndex: language.open ? 60 : undefined }}
                        >
                            <button
                                type='button'
                                aria-label={s.ui.languageAriaLabel}
                                aria-haspopup='listbox'
                                aria-expanded={language.open}
                                onClick={() => language.setOpen((v) => !v)}
                                className={dropdownTriggerClass(language.open, 'gap-2 px-3')}
                            >
                                <Globe className='size-4 shrink-0 text-white/50' aria-hidden='true' />
                                <span>{localeLabels[locale]}</span>
                                <ChevronDown aria-hidden='true' className={dropdownChevronClass(language.open)} />
                            </button>

                            {language.open && (
                                <div
                                    role='listbox'
                                    aria-label={s.ui.languageHeading}
                                    className={cn('absolute top-[calc(100%+8px)] right-0 w-48', DROPDOWN_MENU_CLASS)}
                                >
                                    <div className='p-1.5'>
                                        {locales.map((loc) => {
                                            const active = loc === locale

                                            return (
                                                <Link
                                                    key={loc}
                                                    href={router.asPath}
                                                    locale={loc}
                                                    hrefLang={loc}
                                                    role='option'
                                                    aria-selected={active}
                                                    onClick={() => language.setOpen(false)}
                                                    className={dropdownRowClass(active)}
                                                >
                                                    <span className='truncate'>{localeNames[loc]}</span>
                                                    {active && (
                                                        <Check
                                                            className={DROPDOWN_CHECK_CLASS}
                                                            aria-hidden='true'
                                                        />
                                                    )}
                                                </Link>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div
                            className={cn('menu-button w-nav-button', menuOpen && 'w--open')}
                            role='button'
                            tabIndex={0}
                            aria-label={s.ui.menuAriaLabel}
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen((v) => !v)}
                            onKeyDown={(e) => e.key === 'Enter' && setMenuOpen((v) => !v)}
                        >
                            <div className='center-box'>
                                <div ref={hamburgerRef} className='hamburger-lottie' />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='navbar-line' />
        </div>
    )
}
